import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAvailableProducts,
  getSubscriptionDetails,
  createSubscriptionCheckout,
  formatPrice,
  formatDate,
  getStatusBadge
} from '../../services/subscriptionService';
import type { StripeProduct, AgentSubscription } from '../../types/subscription';
import '../../css/subscription.css';

const SubscriptionPage: React.FC = () => {
  const { currentAgent, refreshSubscriptionStatus } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<AgentSubscription | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);

  // Use ref to avoid infinite loop with refreshSubscriptionStatus in useCallback
  const refreshSubscriptionStatusRef = useRef(refreshSubscriptionStatus);
  refreshSubscriptionStatusRef.current = refreshSubscriptionStatus;

  const loadSubscriptionData = useCallback(async () => {
    // Refresh subscription status from Firestore
    await refreshSubscriptionStatusRef.current();
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription details
      const details = await getSubscriptionDetails();
      setSubscription(details.subscription);
      setPortalUrl(details.portalUrl || null);

      // If no subscription, fetch available products
      if (!details.subscription) {
        const productsData = await getAvailableProducts();
        setProducts(productsData.products);
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check URL for success/cancel parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setError(null);
      // Show success message, then reload after a delay
      setTimeout(() => {
        window.history.replaceState({}, '', '/agent/subscription');
        loadSubscriptionData();
      }, 2000);
    } else if (urlParams.get('canceled') === 'true') {
      setError('Subscription checkout was canceled');
      window.history.replaceState({}, '', '/agent/subscription');
    }
  }, [loadSubscriptionData]);

  // Load data on mount only
  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  const handleSubscribe = async (priceId: string) => {
    try {
      setCheckingOut(true);
      setError(null);

      const result = await createSubscriptionCheckout(priceId);

      if (result.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError('Failed to start subscription checkout');
      setCheckingOut(false);
    }
  };

  const handleManageSubscription = () => {
    if (portalUrl) {
      window.location.href = portalUrl;
    }
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="loading-container">
          <ProgressSpinner />
          <p>Loading subscription information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="page-header">
        <h1>Agent Subscription</h1>
        <p>Manage your EquityWorkx agent subscription</p>
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />
      )}

      {/* Success Message */}
      {new URLSearchParams(window.location.search).get('success') === 'true' && (
        <Message
          severity="success"
          text="Subscription activated successfully! Welcome aboard."
          style={{ marginBottom: '1rem' }}
        />
      )}

      {subscription ? (
        // Active Subscription View
        <div className="subscription-active">
          <Card className="subscription-card">
            <div className="subscription-header">
              <div>
                <h2>Your Subscription</h2>
                <Tag
                  value={getStatusBadge(subscription.status).label}
                  severity={getStatusBadge(subscription.status).severity}
                  className="status-tag"
                />
              </div>
            </div>

            <Divider />

            <div className="subscription-details">
              <div className="detail-row">
                <span className="detail-label">Plan:</span>
                <span className="detail-value">Annual Subscription</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">{subscription.status}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Current Period:</span>
                <span className="detail-value">
                  {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Next Billing Date:</span>
                <span className="detail-value">
                  {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
              {subscription.cancelAtPeriodEnd && (
                <div className="detail-row">
                  <Message
                    severity="warn"
                    text="Your subscription will cancel at the end of the current period"
                  />
                </div>
              )}
            </div>

            <Divider />

            <div className="subscription-actions">
              <Button
                label="Manage Subscription"
                icon="pi pi-cog"
                onClick={handleManageSubscription}
                className="p-button-primary"
                disabled={!portalUrl}
              />
              <p className="action-hint">
                Update payment method, view billing history, or cancel subscription
              </p>
            </div>
          </Card>

          <Card className="features-card" style={{ marginTop: '1rem' }}>
            <h3>What's Included</h3>
            <ul className="features-list">
              <li>
                <i className="pi pi-check-circle" />
                Full agent dashboard access
              </li>
              <li>
                <i className="pi pi-check-circle" />
                Unlimited customer management
              </li>
              <li>
                <i className="pi pi-check-circle" />
                Advanced analytics and insights
              </li>
              <li>
                <i className="pi pi-check-circle" />
                Lead generation tools
              </li>
              <li>
                <i className="pi pi-check-circle" />
                Priority support
              </li>
            </ul>
          </Card>
        </div>
      ) : (
        // No Subscription - Show Products or Role-based Status
        <div className="subscription-products">
          {currentAgent?.role === 'super_agent' || currentAgent?.role === 'master_agent' ? (
            <div className="subscription-active">
              <Card className="subscription-card">
                <div className="subscription-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ margin: 0 }}>Your Subscription</h2>
                  <Tag
                    value="Active"
                    severity="success"
                    className="status-tag"
                  />
                </div>

                <Divider />

                <div className="subscription-details">
                  <div className="detail-row">
                    <span className="detail-label">Plan:</span>
                    <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                      {currentAgent.role.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </Card>

              <Card className="features-card" style={{ marginTop: '1rem' }}>
                <h3>What's Included</h3>
                <ul className="features-list">
                  <li>
                    <i className="pi pi-check-circle" />
                    Full agent dashboard access
                  </li>
                  <li>
                    <i className="pi pi-check-circle" />
                    Unlimited customer management
                  </li>
                  <li>
                    <i className="pi pi-check-circle" />
                    Advanced analytics and insights
                  </li>
                  <li>
                    <i className="pi pi-check-circle" />
                    Lead generation tools
                  </li>
                  <li>
                    <i className="pi pi-check-circle" />
                    Priority support
                  </li>
                </ul>
              </Card>
            </div>
          ) : products.length === 0 ? (
            <Message
              severity="info"
              text="No subscription plans are currently available. Please contact support."
            />
          ) : (
            <>
              <div className="products-grid">
                {products.map((product) => (
                  <Card key={product.priceId} className="product-card">
                    {product.recommended && (
                      <Tag value="Recommended" className="recommended-tag" severity="success" />
                    )}
                    <div className="product-header">
                      <h3>{product.name}</h3>
                      <div className="product-price">
                        <span className="price-amount">{formatPrice(product.amount)}</span>
                        {product.recurring && (
                          <span className="price-interval">
                            / {product.recurring.interval}
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="product-description">{product.description}</p>

                    {product.features && product.features.length > 0 && (
                      <>
                        <Divider />
                        <ul className="product-features">
                          {product.features.map((feature, index) => (
                            <li key={index}>
                              <i className="pi pi-check" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    <Divider />

                    <Button
                      label={checkingOut ? 'Processing...' : 'Subscribe Now'}
                      icon={checkingOut ? 'pi pi-spin pi-spinner' : 'pi pi-shopping-cart'}
                      onClick={() => handleSubscribe(product.priceId)}
                      disabled={checkingOut}
                      className="p-button-success subscribe-button"
                    />
                  </Card>
                ))}
              </div>

              <Card className="info-card" style={{ marginTop: '2rem' }}>
                <h3>Why Subscribe?</h3>
                <p>
                  As an EquityWorkx agent, your subscription gives you access to powerful tools
                  for managing customers, generating insights, and closing more deals. Our
                  platform helps you stay on top of refinance opportunities and provide
                  exceptional service to your clients.
                </p>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
